import { useRef, useState } from "react";
import { Canvas } from "@react-three/fiber";
import Experience from "./canvas/Experience.jsx";
import Configurator from "./ui/Configurator.jsx";
import InformationModal from "./ui/InformationModal.jsx";
import DrawingModal from "./ui/DrawingModal.jsx";
import InspirationsModal from "./ui/InspirationModal.jsx";
import { cabinetTextures, countertopTextures } from "./data/textureData.js";

const NAV_BUTTONS = ["DRAWING", "INFORMATION", "INSPIRATIONS"];

const INITIAL_TEXTURES = {
  handle: null,
  baseUnit: cabinetTextures.find((t) => t.id === "alpine-white")?.path ?? null,
  wallUnit: cabinetTextures.find((t) => t.id === "alpine-white")?.path ?? null,
  tallUnit: cabinetTextures.find((t) => t.id === "alpine-white")?.path ?? null,
  countertop: countertopTextures.find((t) => t.id === "qw")?.path ?? null,
};

const APP_SETTINGS = window.VMKitchenConfigurator || {};
const USE_DYNAMIC_DATA =
  APP_SETTINGS.useDynamicData === true || APP_SETTINGS.dataSource === "dynamic";

export default function App() {
  const rendererRef = useRef(null);
  const [modelPath, setModelPath] = useState(null);
  const [textures, setTextures] = useState(INITIAL_TEXTURES);
  const [totalPrice, setTotalPrice] = useState(0);
  const [kitchenInfo, setKitchenInfo] = useState(null);
  const [cartConfig, setCartConfig] = useState(null);
  const [cartStatus, setCartStatus] = useState("");
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showDrawingModal, setShowDrawingModal] = useState(false);
  const [showInspirationsModal, setShowInspirationsModal] = useState(false);
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [configLoading, setConfigLoading] = useState(USE_DYNAMIC_DATA);

  function handleTextureChange(key, path) {
    setTextures((prev) => ({ ...prev, [key]: path }));
  }

  function handleCartConfigChange(config) {
    setCartConfig(config);
    setCartStatus("");
  }

  function handleNavClick(label) {
    if (!modelPath) return;
    if (label === "INFORMATION") setShowInfoModal(true);
    if (label === "DRAWING") setShowDrawingModal(true);
    if (label === "INSPIRATIONS") setShowInspirationsModal(true);
  }

  async function handleAddToCart() {
    if (!cartConfig || !window.VMKitchen?.addConfiguredKitchen) return;

    setCartStatus("Adding...");
    try {
      await new Promise((resolve) => requestAnimationFrame(resolve));
      const previewImage = captureCanvasPreview();
      await window.VMKitchen.addConfiguredKitchen({
        ...cartConfig,
        preview_image: previewImage,
      });
      setCartStatus("Added in cart");
    } catch (error) {
      setCartStatus(error.message || "Unable to add to cart");
    }
  }

  function captureCanvasPreview() {
    const canvas = rendererRef.current?.domElement;
    if (!canvas) return "";

    try {
      const maxWidth = 520;
      const scale = Math.min(1, maxWidth / canvas.width);
      const width = Math.max(1, Math.round(canvas.width * scale));
      const height = Math.max(1, Math.round(canvas.height * scale));
      const previewCanvas = document.createElement("canvas");
      previewCanvas.width = width;
      previewCanvas.height = height;
      const context = previewCanvas.getContext("2d");
      if (!context) return "";
      context.fillStyle = "#f9fafb";
      context.fillRect(0, 0, width, height);
      context.drawImage(canvas, 0, 0, width, height);
      return previewCanvas.toDataURL("image/jpeg", 0.82);
    } catch {
      return "";
    }
  }

  return (
    <div className="relative flex flex-col md:flex-row w-full h-full overflow-hidden">
      {/* Canvas */}
      <div
        className={`relative w-full shrink-0 bg-gray-50 transition-all duration-300 ease-in-out md:h-full ${
          panelCollapsed ? "flex-1 md:w-full" : "h-[45vh] md:w-3/5"
        }`}
      >
        <Canvas
          camera={{ fov: 45, near: 0.1, far: 2000, position: [0, 3, 8] }}
          gl={{ preserveDrawingBuffer: true }}
          onCreated={({ gl }) => {
            rendererRef.current = gl;
          }}
        >
          <Experience modelPath={modelPath} textures={textures} />
        </Canvas>

        {/* Panel toggle — desktop only */}
        <button
          onClick={() => setPanelCollapsed((v) => !v)}
          className="absolute right-0 top-1/2 -translate-y-1/2 hidden md:flex items-center justify-center w-4 h-16 z-10 cursor-pointer"
          aria-label={panelCollapsed ? "Expand panel" : "Collapse panel"}
        >
          <div className="w-1.5 h-14 bg-gray-800 rounded-full transition-opacity opacity-60 hover:opacity-100" />
        </button>

        <div className="absolute bottom-4 md:bottom-6 left-0 right-0 flex justify-center gap-2 md:gap-3">
          {NAV_BUTTONS.map((label) => (
            <div key={label} className="relative group">
              <button
                onClick={() => handleNavClick(label)}
                className={`vmkc-primary-button px-3 md:px-5 py-1.5 md:py-2 text-[10px] md:text-xs tracking-widest uppercase rounded-full transition-colors ${
                  modelPath
                    ? "cursor-pointer"
                    : "is-disabled border cursor-default"
                }`}
              >
                {label}
              </button>
              {!modelPath && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  Select a kitchen option
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Configurator panel */}
      <div
        className={`border-gray-200 flex flex-col min-h-0 overflow-hidden transition-all duration-300 ease-in-out w-full border-t md:border-t-0 ${
          panelCollapsed
            ? "shrink-0 md:w-0 md:border-0"
            : "flex-1 md:w-2/5 md:border-l"
        }`}
      >
        {/* Panel toggle — mobile only */}
        <button
          onClick={() => setPanelCollapsed((v) => !v)}
          className="flex md:hidden items-center justify-center w-full h-4 shrink-0 cursor-pointer"
          aria-label={panelCollapsed ? "Expand panel" : "Collapse panel"}
        >
          <div className="w-14 h-1.5 bg-gray-800 rounded-full transition-opacity opacity-60 hover:opacity-100" />
        </button>

        <div
          className={`flex-1 overflow-y-auto min-w-[280px] ${panelCollapsed ? "hidden md:block" : ""}`}
        >
          <Configurator
            onModelSelect={setModelPath}
            onTextureChange={handleTextureChange}
            onPriceChange={setTotalPrice}
            onInfoChange={setKitchenInfo}
            onCartConfigChange={handleCartConfigChange}
            onLoadingChange={setConfigLoading}
          />
        </div>
        <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-t border-gray-200 bg-white shrink-0 min-w-[280px]">
          <span className="text-sm text-gray-900">
            <span className="font-bold">TOTAL:</span> QAR{" "}
            {totalPrice.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
          <button
            onClick={handleAddToCart}
            disabled={!cartConfig}
            className={`vmkc-primary-button px-4 md:px-5 py-2 text-sm tracking-wide transition-colors ${
              cartConfig
                ? "cursor-pointer"
                : "cursor-not-allowed"
            }`}
          >
            {cartStatus || "Add to cart"}
          </button>
        </div>
      </div>

      {configLoading && (
        <div className="vmkc-app-loader" role="status" aria-label="Loading">
          <div className="vmkc-app-loader__mark" aria-hidden="true">
            <span className="vmkc-app-loader__ring" />
            <span className="vmkc-app-loader__core" />
          </div>
        </div>
      )}

      {showInfoModal && (
        <InformationModal
          info={kitchenInfo}
          onClose={() => setShowInfoModal(false)}
        />
      )}

      {showDrawingModal && (
        <DrawingModal
          info={kitchenInfo}
          onClose={() => setShowDrawingModal(false)}
        />
      )}

      {showInspirationsModal && (
        <InspirationsModal
          info={kitchenInfo}
          onClose={() => setShowInspirationsModal(false)}
        />
      )}
    </div>
  );
}
